import React, { useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter
} from '@/components/ui/card'
import { LineChart, BarChart } from '@/components/ui/charts'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { CalendarIcon, ChevronUpIcon, ChevronDownIcon, MinusIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { DateRange } from 'react-day-picker'

export interface DataPoint {
  label: string
  value: number
}

export interface MetricCard {
  title?: string
  label?: string // Support both title and label for compatibility
  value: string | number
  change?: string | number
  trend?: 'up' | 'down' | 'neutral'
  status?: 'positive' | 'negative' | 'neutral' // Support status field from system prompt
  unit?: string
  description?: string
  type?: string // To identify section headers or other special types
}

export interface FilterOption {
  value: string
  label: string
}

export interface FilterItem {
  name: string
  label: string
  type: 'select' | 'daterange' | 'checkbox' | 'radio'
  options?: string[] | FilterOption[]
  default?: string | { start: string; end: string }
}

export interface ChartData {
  type: 'line' | 'bar'
  title: string
  description?: string
  data: {
    labels: string[]
    datasets: {
      label: string
      data: number[]
      borderColor: string
      backgroundColor: string
    }[]
  }
}

export interface DataTable {
  title: string
  description?: string
  headers: string[]
  rows: (string | number)[][]
}

export interface DataDashboardData {
  title: string
  description?: string
  metrics?: MetricCard[]
  charts?: ChartData[]
  tables?: DataTable[]
  tags?: string[]
  filters?: FilterItem[]
  footer?: string
}

const TrendBadge: React.FC<{
  trend?: 'up' | 'down' | 'neutral'
  status?: 'positive' | 'negative' | 'neutral'
  change?: string | number
}> = ({ trend, status, change }) => {
  if ((!trend && !status) || !change) return null

  // Determine the status based on trend or status prop
  const effectiveStatus =
    status || (trend === 'up' ? 'positive' : trend === 'down' ? 'negative' : 'neutral')

  const variants = {
    positive: 'bg-green-50 text-green-700 border-green-200',
    negative: 'bg-red-50 text-red-700 border-red-200',
    neutral: 'bg-gray-50 text-gray-700 border-gray-200'
  }

  const icons = {
    positive: <ChevronUpIcon className="size-3" />,
    negative: <ChevronDownIcon className="size-3" />,
    neutral: <MinusIcon className="size-3" />
  }

  return (
    <span
      className={`rounded-full border px-2 py-1 text-xs ${variants[effectiveStatus]} ml-2 inline-flex items-center`}
    >
      {icons[effectiveStatus]}
      <span className="ml-1">{change}</span>
    </span>
  )
}

const DateRangeFilter: React.FC<{ filter: FilterItem; onChange: (value: any) => void }> = ({
  filter,
  onChange
}) => {
  const defaultDates = typeof filter.default === 'object' ? filter.default : { start: '', end: '' }
  const [date, setDate] = useState<DateRange | undefined>({
    from: defaultDates.start ? new Date(defaultDates.start) : undefined,
    to: defaultDates.end ? new Date(defaultDates.end) : undefined
  })

  const handleSelect = (range: DateRange | undefined) => {
    setDate(range)
    if (range) {
      onChange({
        start: range.from ? format(range.from, 'yyyy-MM-dd') : '',
        end: range.to ? format(range.to, 'yyyy-MM-dd') : ''
      })
    } else {
      onChange({ start: '', end: '' })
    }
  }

  return (
    <div className="flex flex-col space-y-1.5">
      <label className="text-sm font-medium">{filter.label}</label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full justify-start text-left font-normal',
              !date?.from && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 size-4" />
            {date?.from
              ? date.to
                ? `${format(date.from, 'PP')} - ${format(date.to, 'PP')}`
                : format(date.from, 'PP')
              : 'Select date range'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="range" selected={date} onSelect={handleSelect} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  )
}

const SelectFilter: React.FC<{ filter: FilterItem; onChange: (value: string) => void }> = ({
  filter,
  onChange
}) => {
  const [value, setValue] = useState<string>((filter.default as string) || '')

  const handleValueChange = (newValue: string) => {
    setValue(newValue)
    onChange(newValue)
  }

  const options = Array.isArray(filter.options) ? filter.options : []

  return (
    <div className="flex flex-col space-y-1.5">
      <label className="text-sm font-medium">{filter.label}</label>
      <Select value={value} onValueChange={handleValueChange}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={`Select ${filter.label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option, index) => {
            const optionValue = typeof option === 'string' ? option : option.value
            const optionLabel = typeof option === 'string' ? option : option.label

            return (
              <SelectItem key={index} value={optionValue}>
                {optionLabel}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </div>
  )
}

export const DataDashboard: React.FC<{ data: DataDashboardData }> = ({ data }) => {
  const [filterValues, setFilterValues] = useState<Record<string, any>>({})

  // Log dashboard rendering for debugging
  React.useEffect(() => {
    if (data) {
      console.log('DataDashboard rendering with data:', {
        title: data.title,
        metrics: data.metrics?.length || 0,
        charts: data.charts?.length || 0,
        filters: data.filters?.length || 0
      })
    } else {
      console.error('DataDashboard received null or undefined data')
    }
  }, [data])

  // Error handling for invalid data
  if (!data) {
    return (
      <Card className="my-4 overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle>Error: Invalid Dashboard Data</CardTitle>
          <CardDescription>
            Dashboard could not be displayed due to missing or invalid data.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const handleFilterChange = (name: string, value: any) => {
    setFilterValues((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  return (
    <Card className="my-4 overflow-hidden">
      <CardHeader className="px-4 pb-2 sm:px-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg sm:text-xl">{data.title || 'Data Dashboard'}</CardTitle>
            {data.description && (
              <CardDescription className="mt-1 text-sm">{data.description}</CardDescription>
            )}
          </div>

          {data.tags && data.tags.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {data.tags.map((tag, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardHeader>

      {/* Filters */}
      {data.filters && data.filters.length > 0 && (
        <div className="border-b px-4 pb-4 pt-0 sm:px-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
            {data.filters.map((filter, index) => (
              <div key={index}>
                {filter.type === 'daterange' && (
                  <DateRangeFilter
                    filter={filter}
                    onChange={(value) => handleFilterChange(filter.name, value)}
                  />
                )}
                {filter.type === 'select' && (
                  <SelectFilter
                    filter={filter}
                    onChange={(value) => handleFilterChange(filter.name, value)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <CardContent className="p-4 sm:p-6">
        {/* Metrics Cards */}
        {data.metrics && data.metrics.length > 0 && (
          <div className="space-y-4 sm:space-y-6">
            {/* Group metrics by section headers or show all */}
            {(() => {
              // Define types for grouping
              type MetricGroup = {
                section: MetricCard | null
                metrics: MetricCard[]
              }

              const metrics = data.metrics || []
              let currentSection: MetricCard | null = null
              let currentMetrics: MetricCard[] = []
              const groupedMetrics: MetricGroup[] = []

              // Separate section headers from regular metrics
              metrics.forEach((metric, idx) => {
                if (metric.type === 'section_header') {
                  // If we have metrics from a previous section, add them
                  if (currentMetrics.length > 0) {
                    groupedMetrics.push({
                      section: currentSection,
                      metrics: [...currentMetrics]
                    })
                    currentMetrics = []
                  }

                  // Start a new section
                  currentSection = metric
                } else {
                  // Add to current section
                  currentMetrics.push(metric)
                }

                // Handle the last group
                if (idx === metrics.length - 1 && currentMetrics.length > 0) {
                  groupedMetrics.push({
                    section: currentSection,
                    metrics: [...currentMetrics]
                  })
                }
              })

              // If we have no groupings, create one with all metrics
              if (groupedMetrics.length === 0 && currentMetrics.length > 0) {
                groupedMetrics.push({
                  section: null,
                  metrics: [...currentMetrics]
                })
              }

              return groupedMetrics.map((group, groupIdx) => (
                <div key={`metric-group-${groupIdx}`} className="space-y-3 sm:space-y-4">
                  {group.section && (
                    <div className="border-b pb-2">
                      <h3 className="text-base font-medium sm:text-lg">
                        {group.section.label || group.section.title || 'Section'}
                      </h3>
                      {typeof group.section.value === 'string' &&
                        group.section.value.length > 0 && (
                          <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
                            {group.section.value}
                          </p>
                        )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
                    {group.metrics.map((metric, metricIdx) => (
                      <Card key={`metric-${groupIdx}-${metricIdx}`} className="bg-card/50">
                        <CardContent className="p-3 sm:p-4">
                          <div className="text-muted-foreground text-xs font-medium sm:text-sm">
                            {metric.title || metric.label}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center">
                            <div className="text-xl font-bold sm:text-2xl">
                              {metric.value}
                              {metric.unit && (
                                <span className="ml-1 text-xs font-normal sm:text-sm">
                                  {metric.unit}
                                </span>
                              )}
                            </div>
                            <TrendBadge
                              trend={metric.trend}
                              status={metric.status}
                              change={metric.change}
                            />
                          </div>
                          {metric.description && (
                            <div className="text-muted-foreground mt-2 text-xs">
                              {metric.description}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))
            })()}
          </div>
        )}

        {/* Charts */}
        {data.charts && data.charts.length > 0 && (
          <div className={`mt-4 sm:mt-6 ${data.charts.length > 1 ? 'mb-4 sm:mb-6' : ''}`}>
            {data.charts.length === 1 ? (
              <div className="h-[250px] w-full sm:h-[300px] md:h-[350px]">
                {renderChart(data.charts[0])}
              </div>
            ) : (
              <Tabs defaultValue="chart-0" className="w-full">
                <TabsList className="mb-2 flex w-full overflow-x-auto sm:w-auto">
                  {data.charts.map((chart, index) => (
                    <TabsTrigger key={index} value={`chart-${index}`} className="shrink-0">
                      {chart.title}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {data.charts.map((chart, index) => (
                  <TabsContent
                    key={index}
                    value={`chart-${index}`}
                    className="h-[250px] sm:h-[300px] md:h-[350px]"
                  >
                    {renderChart(chart)}
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </div>
        )}

        {/* Tables */}
        {data.tables && data.tables.length > 0 && (
          <div className="mt-4 sm:mt-6">
            {data.tables.length === 1 ? (
              <div className="overflow-x-auto">{renderTable(data.tables[0])}</div>
            ) : (
              <Tabs defaultValue="table-0" className="w-full">
                <TabsList className="mb-2 flex w-full overflow-x-auto sm:w-auto">
                  {data.tables.map((table, index) => (
                    <TabsTrigger key={index} value={`table-${index}`} className="shrink-0">
                      {table.title}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {data.tables.map((table, index) => (
                  <TabsContent key={index} value={`table-${index}`} className="overflow-x-auto">
                    {renderTable(table)}
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </div>
        )}

        {(!data.metrics || data.metrics.length === 0) &&
          (!data.charts || data.charts.length === 0) &&
          (!data.tables || data.tables.length === 0) && (
            <p className="text-muted-foreground text-sm">
              No data available for display. Provide metrics, charts, or tables in the data
              structure.
            </p>
          )}
      </CardContent>

      {data.footer && (
        <CardFooter className="text-muted-foreground border-t px-4 py-3 text-xs sm:px-6">
          {data.footer}
        </CardFooter>
      )}
    </Card>
  )
}

function renderChart(chart: ChartData) {
  if (chart.type === 'line') {
    return <LineChart data={chart.data} />
  } else if (chart.type === 'bar') {
    // Transform the data to match the BarChart component's expected format
    const barData = chart.data.labels.map((label, index) => ({
      workspace: label,
      rate: chart.data.datasets[0].data[index]
    }))
    return <BarChart data={barData} />
  }
  return null
}

function renderTable(table: DataTable) {
  return (
    <div>
      {table.description && (
        <p className="text-muted-foreground mb-2 text-sm">{table.description}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              {table.headers.map((header, index) => (
                <th key={index} className="text-muted-foreground px-3 py-2 text-left font-medium">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-muted/50 border-b last:border-0">
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="px-3 py-2">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DataDashboard
